import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import { Shield, Sparkles, ArrowRight, Trash2, AlertTriangle, Share2, HeartPulse, Database, Settings as Cog, Mail, Receipt, LogOut, Download, Loader2 } from "lucide-react";
import PasswordPromptModal from "@/components/PasswordPromptModal";
import DeleteAccountModal from "@/components/DeleteAccountModal";
import { useCurrency } from "@/components/Price";

const PLAN_LABEL = { free: "Gratuito", personal: "Personal", personal_annual: "Personal Anual", lifetime: "De por Vida" };
const PLAN_PRICE_TPL = { free: "{S}0", personal: "{S}7", personal_annual: "{S}70", lifetime: "{S}149" };

export default function Settings() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { symbol } = useCurrency();
  // Translated plan labels — fallback to ES if key missing
  const planLabel = {
    free: t("plan_free"),
    personal: t("plan_personal"),
    personal_annual: t("plan_personal") + " " + (t("pricing_billing_annual") || "Anual"),
    lifetime: t("pricing_lifetime_label"),
  };
  const planSuffix = {
    free: t("set_per_month"),
    personal: t("set_per_month"),
    personal_annual: t("set_per_year"),
    lifetime: " " + t("set_pay_once"),
  };
  const PLAN_PRICE_BASE = Object.fromEntries(Object.entries(PLAN_PRICE_TPL).map(([k, v]) => [k, v.replace("{S}", symbol)]));
  const PLAN_PRICE = Object.fromEntries(Object.keys(PLAN_PRICE_BASE).map((k) => [k, `${PLAN_PRICE_BASE[k]}${planSuffix[k]}`]));
  const plan = user?.plan || "free";
  const [limits, setLimits] = useState(null);
  const [storage, setStorage] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/limits").then((r) => setLimits(r.data)).catch(() => {});
    api.get("/storage/usage").then((r) => setStorage(r.data)).catch(() => {});
    api.get("/admin/me").then(() => setIsAdmin(true)).catch(() => {});
    api.get("/payments/history").then((r) => setPayments(r.data?.items || [])).catch(() => {});
  }, []);

  const shareApp = async () => {
    const data = {
      title: "KeepSoul",
      text: "Acabo de proteger mi legado digital con KeepSoul. Tu familia merece estar protegida también.",
      url: "https://keepsoul.app",
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
    } catch (_) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(`${data.text} ${data.url}`);
      toast.success(t("settings_share_copied"));
    } catch {
      toast.error(t("settings_share_failed"));
    }
  };

  const downgradeToFree = async () => {
    if (!window.confirm(t("settings_downgrade_confirm"))) return;
    try {
      await api.put("/auth/plan", { plan: "free", confirm_cancel: true });
      toast.success(t("settings_downgrade_done"));
      setTimeout(() => window.location.reload(), 700);
    } catch {
      toast.error(t("settings_generic_error"));
    }
  };

  const openCustomerPortal = async () => {
    try {
      const r = await api.post("/payments/customer-portal");
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : t("settings_portal_open_failed");
      toast.error(msg, { duration: 5000 });
    }
  };

  const deleteAccount = () => setDeleteModalOpen(true);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success(t("settings_delete_done"), { duration: 4000 });
      setTimeout(() => (window.location.href = "/"), 1500);
    } catch {
      toast.error(t("settings_delete_failed"));
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  const exportData = async () => {
    if (user?.auth_method === "google" || user?.auth_method === "oauth") {
      toast.error(t("settings_export_google_warn"), { duration: 6000 });
      return;
    }
    setExportModalOpen(true);
  };

  const submitExport = async (pwd) => {
    setExporting(true);
    try {
      const r = await api.post("/export", { password: pwd });
      const payload = r.data;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
      a.href = url;
      a.download = `keepsoul-legacy-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings_export_started"), { duration: 5000 });
      setExportModalOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg = t("settings_export_failed");
      if (status === 401) msg = t("settings_password_incorrect");
      else if (status === 403) msg = typeof detail === "string" ? detail : t("settings_export_not_authorized");
      else if (status === 429) msg = t("settings_export_quota_exhausted");
      else if (typeof detail === "string") msg = detail;
      toast.error(msg, { duration: 6000 });
    } finally {
      setExporting(false);
    }
  };

  const submitExportEmail = async (pwd) => {
    setExporting(true);
    try {
      const r = await api.post("/export/email", { password: pwd });
      const masked = r?.data?.email_sent_to || t("settings_export_your_email_fallback");
      toast.success(t("settings_export_link_sent").replace("{EMAIL}", masked), { duration: 7000 });
      setExportModalOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg = t("settings_export_link_failed");
      if (status === 401) msg = t("settings_password_incorrect");
      else if (status === 403) msg = typeof detail === "string" ? detail : t("settings_unauthorized");
      else if (status === 429) msg = t("settings_export_too_many_requests");
      else if (typeof detail === "string") msg = detail;
      toast.error(msg, { duration: 6000 });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title={t("nav_settings")} />

      <div className="space-y-6 max-w-2xl">
        {/* Plan card */}
        <div className="glass-card rounded-md p-8 gold-glow" data-testid="settings-plan-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-label text-gold mb-2 flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> {t("plan_current")}
              </div>
              <div className="font-display text-4xl text-gold-light mb-1">
                {planLabel[plan]}
              </div>
              <div className="text-sm text-muted">{PLAN_PRICE[plan]}</div>
            </div>
            <Link to="/plans" data-testid="settings-change-plan">
              <button className="btn-gold">
                {t("plan_change")} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
          {(plan === "personal" || plan === "personal_annual") && (
            <>
              <div className="divider-gold" />
              <button
                onClick={downgradeToFree}
                className="text-sm text-faded hover:text-red-400 transition"
                data-testid="cancel-subscription"
              >
                {t("set_cancel_sub")}
              </button>
            </>
          )}
          {plan === "free" && (
            <>
              <div className="divider-gold" />
              <button
                onClick={async () => {
                  try {
                    const r = await api.post("/payments/reclaim");
                    if (r.data?.recovered?.length > 0) {
                      toast.success(`${t("plan_current")}: ${r.data.plan}`);
                      setTimeout(() => window.location.reload(), 800);
                    } else {
                      toast.info(t("set_no_recent_payments"));
                    }
                  } catch {
                    toast.error(t("set_recover_error"));
                  }
                }}
                className="text-sm text-faded hover:text-gold transition"
                data-testid="reclaim-payment-btn"
              >
                {t("set_pay_recover_q")} {t("set_pay_recover_link")}
              </button>
            </>
          )}
        </div>

        {/* Payment history */}
        {(payments.length > 0 || plan !== "free") && (
          <div className="glass-card rounded-md p-6" data-testid="settings-payments-card">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-gold" />
              <h3 className="font-display text-xl text-gold-light">Historial de pagos</h3>
            </div>
            {payments.length === 0 ? (
              <p className="text-sm text-faded">Aún no tienes pagos registrados.</p>
            ) : (
              <ul className="divide-y divide-gold-subtle/20">
                {payments.map((p) => (
                  <li key={p.session_id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid="payment-row">
                    <div>
                      <div className="text-gold-light">{p.plan_label || p.plan}</div>
                      <div className="text-xs text-faded">
                        {p.created_at ? new Date(p.created_at).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" }) : "—"}
                        {p.discount_code && (
                          <span className="ml-2 text-emerald-400">· {p.discount_code} (-{p.discount_percent}%)</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-gold-light font-medium">{p.amount?.toFixed?.(2) ?? p.amount} {p.currency}</div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-400">Pagado</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-faded mt-4">
              Los recibos oficiales se gestionan directamente en Stripe. Si necesitas una factura, escríbenos a
              <a href="mailto:support@keepsoul.app" className="text-gold ml-1">support@keepsoul.app</a>.
            </p>
            {plan !== "free" && plan !== "lifetime" && plan !== "founders" && (
              <div className="mt-4 pt-4 border-t border-gold-subtle/20">
                <button
                  onClick={openCustomerPortal}
                  className="btn-ghost text-sm w-full sm:w-auto"
                  data-testid="customer-portal-btn"
                >
                  Gestionar suscripción en Stripe →
                </button>
                <p className="text-[11px] text-faded mt-2">
                  Actualiza tu método de pago, cambia tu plan o cancela cuando quieras.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Quick access */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/proof-of-life" className="glass-card rounded-md p-4 flex items-center gap-3 hover:border-gold transition" data-testid="quick-pol">
            <HeartPulse className="w-5 h-5 text-gold" />
            <div>
              <div className="text-gold-light text-sm">{t("set_pol_card_title")}</div>
              <div className="text-xs text-muted">{t("set_pol_card_sub")}</div>
            </div>
          </Link>
          <Link to="/digital-assets" className="glass-card rounded-md p-4 flex items-center gap-3 hover:border-gold transition" data-testid="quick-assets">
            <Database className="w-5 h-5 text-gold" />
            <div>
              <div className="text-gold-light text-sm">{t("set_assets_card_title")}</div>
              <div className="text-xs text-muted">{t("set_assets_card_sub")}</div>
            </div>
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              className="glass-card rounded-md p-4 flex items-center gap-3 hover:border-gold transition border-gold/30 sm:col-span-2"
              data-testid="quick-admin"
            >
              <Cog className="w-5 h-5 text-gold" />
              <div className="flex-1">
                <div className="text-gold-light text-sm flex items-center gap-2">
                  Panel de Administración
                  <span className="text-[9px] uppercase tracking-widest bg-gold/15 text-gold px-1.5 py-0.5 rounded">Admin</span>
                </div>
                <div className="text-xs text-muted">Métricas de usuarios, ingresos, emails y sistema</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faded" />
            </Link>
          )}
        </div>

        {/* Share KeepSoul */}
        <button
          onClick={shareApp}
          className="w-full glass-card rounded-md p-5 flex items-center justify-between hover:border-gold transition text-left group"
          data-testid="share-keepsoul-btn"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle">
              <Share2 className="w-5 h-5 text-gold" strokeWidth={1.4} />
            </div>
            <div>
              <div className="text-gold-light">{t("set_share_title")}</div>
              <div className="text-xs text-muted mt-0.5">{t("set_share_sub")}</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-faded group-hover:text-gold transition" />
        </button>

        {/* Storage indicator */}
        {storage && (
          <div className="glass-card rounded-md p-6" data-testid="storage-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl text-gold-light">{t("set_storage_label")}</h2>
              <span className="text-sm text-gold-light font-mono">
                {storage.used_mb} MB / {storage.quota_mb >= 1024 ? `${(storage.quota_mb/1024).toFixed(0)} GB` : `${storage.quota_mb} MB`}
              </span>
            </div>
            <div className="ks-progress">
              <div
                className={`ks-progress-fill ${
                  storage.percent >= 100 ? "danger" : storage.percent >= 80 ? "warn" : ""
                }`}
                style={{ width: `${Math.min(100, storage.percent)}%` }}
                data-testid="storage-progress"
              />
            </div>
            <p className="text-xs text-faded mt-3">
              {t("set_storage_help").replace("{PCT}", storage.percent)}
              {storage.percent > 80 && plan === "free" && (
                <> · <Link to="/plans" className="text-gold hover:text-gold-light">{t("set_upgrade_plan")}</Link></>
              )}
            </p>
          </div>
        )}

        {/* Plan usage */}
        {limits && (
          <div className="glass-card rounded-md p-8" data-testid="usage-card">
            <h2 className="font-display text-2xl text-gold-light mb-6">{t("set_plan_usage_title")}</h2>
            <div className="space-y-4">
              {[
                ["vault", t("set_usage_vault")],
                ["messages", t("set_usage_messages")],
                ["heirs", t("set_usage_heirs")],
                ["contacts", t("set_usage_contacts")],
              ].map(([k, label]) => {
                const cap = limits.limits[k];
                const used = limits.usage[k];
                const pct = cap ? Math.min(100, (used / cap) * 100) : 0;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted">{label}</span>
                      <span className="text-gold-light">
                        {used} / {cap ?? "∞"}
                      </span>
                    </div>
                    {cap !== null && (
                      <div className="ks-progress">
                        <div
                          className={`ks-progress-fill ${
                            pct >= 100 ? "danger" : pct >= 80 ? "warn" : ""
                          }`}
                          style={{ width: `${pct}%` }}
                          data-testid={`usage-progress-${k}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Profile */}
        <div className="glass-card rounded-md p-8 space-y-6">
          <div>
            <div className="font-label text-faded mb-2">{t("name")}</div>
            <div className="text-gold-light text-xl">{user?.name}</div>
          </div>
          <div>
            <div className="font-label text-faded mb-2">{t("email")}</div>
            <div className="text-gold-light text-xl break-all">{user?.email}</div>
          </div>
          <div className="divider-gold" />
          <VaultLockSettings t={t} />
          <div className="divider-gold" />
          <div>
            <div className="font-label text-faded mb-3">Idioma / Language / 语言 / 언어</div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {[
                { code: "es", label: "Español" },
                { code: "en", label: "English" },
                { code: "zh", label: "中文" },
                { code: "ko", label: "한국어" },
              ].map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={lang === l.code ? "btn-gold" : "btn-ghost"}
                  data-testid={`settings-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="divider-gold" />
          <div className="flex items-center gap-3 text-muted text-sm">
            <Shield className="w-4 h-4 text-gold" />
            AES-256 · Datos cifrados en reposo · GDPR-compliant
          </div>
          <button
            onClick={logout}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition text-sm"
            data-testid="settings-logout"
          >
            <LogOut className="w-4 h-4" /> {t("nav_logout")}
          </button>
        </div>

        {/* Support */}
        <div className="glass-card rounded-md p-8" data-testid="settings-support-card">
          <div className="flex items-start gap-4 mb-4">
            <Mail className="w-5 h-5 text-gold mt-1 shrink-0" />
            <div className="flex-1">
              <div className="font-label text-gold mb-1">Soporte</div>
              <p className="text-muted text-sm leading-relaxed">
                Para cualquier duda o problema, escríbenos a{" "}
                <a href="mailto:support@keepsoul.app" className="text-gold-light hover:underline">
                  support@keepsoul.app
                </a>. Te respondemos en menos de 24 h.
              </p>
            </div>
          </div>
          <a
            href="mailto:support@keepsoul.app?subject=Consulta%20-%20KeepSoul"
            className="btn-gold w-full justify-center sm:w-auto sm:inline-flex"
            data-testid="settings-support-mail"
          >
            <Mail className="w-4 h-4" /> Enviar email
          </a>
        </div>

        {/* GDPR Export */}
        <div className="glass-card rounded-md p-8" data-testid="settings-gdpr-export-card">
          <div className="font-label text-gold mb-4 flex items-center gap-2">
            <Download className="w-3 h-3" /> Exportar mis datos · GDPR Art. 20
          </div>
          <p className="text-sm text-muted mb-5 leading-relaxed">
            Descarga todo tu legado en un archivo <code className="text-gold/90">.json</code> descifrado:
            bóveda, mensajes, herederos, activos digitales, contactos, cápsulas e historias.
            Verifica tu contraseña antes para proteger la operación.
          </p>
          <button
            onClick={exportData}
            disabled={exporting}
            className="btn-ghost"
            data-testid="settings-gdpr-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? "Preparando…" : "Descargar mi legado"}
          </button>
          <p className="text-[11px] text-faded mt-3">
            Límite: 1 exportación cada 24 h. Te enviaremos un email de notificación de seguridad.
          </p>
        </div>

        {/* Danger zone */}
        <div className="glass-card rounded-md p-8 border-red-900/50">
          <div className="font-label text-red-400 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Zona peligrosa
          </div>
          <p className="text-sm text-muted mb-5 leading-relaxed">
            Eliminar tu cuenta borra todos tus datos para siempre. Esta acción es irreversible y se
            cumple con el GDPR (derecho al olvido).
          </p>
          <button
            onClick={deleteAccount}
            disabled={deleting}
            className="btn-danger"
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4" />
            {deleting ? "Eliminando..." : "Eliminar mi cuenta"}
          </button>
        </div>
      </div>
      <PasswordPromptModal
        open={exportModalOpen}
        onClose={() => !exporting && setExportModalOpen(false)}
        onSubmit={submitExport}
        onSubmitEmail={submitExportEmail}
        busy={exporting}
      />
      <DeleteAccountModal
        open={deleteModalOpen}
        onClose={() => !deleting && setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        busy={deleting}
      />
    </div>
  );
}

function VaultLockSettings({ t }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem("ks_vault_lock_enabled") === "true"; }
    catch { return false; }
  });
  const [minutes, setMinutes] = useState(() => {
    try { return parseInt(localStorage.getItem("ks_vault_lock_minutes") || "5", 10); }
    catch { return 5; }
  });

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    try {
      localStorage.setItem("ks_vault_lock_enabled", next ? "true" : "false");
      // Lock immediately when turning ON, clear stale unlock when turning OFF
      sessionStorage.removeItem("ks_vault_unlocked_at");
    } catch { /* noop */ }
  };

  const setM = (m) => {
    const v = Math.max(1, Math.min(60, parseInt(m, 10) || 5));
    setMinutes(v);
    try { localStorage.setItem("ks_vault_lock_minutes", String(v)); }
    catch { /* noop */ }
  };

  return (
    <div data-testid="vault-lock-settings">
      <div className="font-label text-faded mb-2">{t("vault_lock_setting_title")}</div>
      <p className="text-muted text-xs mb-3">{t("vault_lock_setting_body")}</p>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="vault-lock-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={toggle}
            className="w-4 h-4 accent-gold"
            data-testid="vault-lock-checkbox"
          />
          <span className="text-bone text-sm">
            {enabled ? t("vault_lock_enabled") : t("vault_lock_disabled")}
          </span>
        </label>
        {enabled && (
          <div className="flex items-center gap-2" data-testid="vault-lock-minutes-wrap">
            <span className="text-muted text-xs">{t("vault_lock_minutes_label")}</span>
            <input
              type="number"
              min={1}
              max={60}
              value={minutes}
              onChange={(e) => setM(e.target.value)}
              className="input-field w-20 text-center"
              data-testid="vault-lock-minutes-input"
            />
          </div>
        )}
      </div>
    </div>
  );
}

